/**
 * Campaign Detail Page Module
 *
 * Handles campaign status polling, email previews, pause/resume,
 * and other campaign detail page interactions.
 *
 * @module components/campaign-detail
 */

import { getConfig, getCsrfToken } from '@/utils/template-data.js';

// Module state
let statusPollInterval = null;
let pollRetryCount = 0;
const MAX_POLL_RETRIES = 10;
let currentCampaignId = null;

/**
 * Initialize campaign detail functionality.
 * @param {Object} config - Configuration from template
 * @param {number} config.campaignId - Campaign ID
 * @param {string} config.campaignStatus - Current campaign status
 */
export function initCampaignDetail(config) {
  const { campaignId, campaignStatus } = config;
  currentCampaignId = campaignId;

  // Start polling if in a processing state
  if (['sampling', 'generating_all'].includes(campaignStatus)) {
    startStatusPolling(campaignId);
  }

  // Animate counters
  const counters = document.querySelectorAll('.metric-value[data-count]');
  counters.forEach(counter => {
    const target = parseInt(counter.getAttribute('data-count'));
    if (target > 0) {
      animateValue(counter, 0, target, 1000);
    }
  });

  // Clean up on page unload
  window.addEventListener('beforeunload', stopStatusPolling);

  // Expose functions globally for onclick handlers
  window.toggleQuickActions = toggleQuickActions;
  window.previewEmail = previewEmail;
  window.pauseCampaign = () => pauseCampaignAction(campaignId);
  window.resumeCampaign = () => resumeCampaignAction(campaignId);
  window.confirmDelete = confirmDelete;
}

/**
 * Start polling for campaign status updates.
 * @param {number} campaignId - Campaign ID
 */
export function startStatusPolling(campaignId) {
  console.log('Starting status polling for campaign:', campaignId);

  // Clear any existing interval
  if (statusPollInterval) {
    clearInterval(statusPollInterval);
  }

  // Poll every 2 seconds
  statusPollInterval = setInterval(() => {
    pollCampaignStatus(campaignId);
  }, 2000);

  // Do an immediate poll
  pollCampaignStatus(campaignId);
}

/**
 * Stop status polling.
 */
export function stopStatusPolling() {
  console.log('Stopping status polling');
  if (statusPollInterval) {
    clearInterval(statusPollInterval);
    statusPollInterval = null;
  }
  pollRetryCount = 0;
}

/**
 * Poll campaign status.
 * @param {number} campaignId - Campaign ID
 */
function pollCampaignStatus(campaignId) {
  fetch(`/dashboard/campaigns/${campaignId}/sample/`, {
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
    }
  })
    .then(response => {
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      return response.json();
    })
    .then(data => {
      console.log('Poll response:', data);

      // Reset retry count on successful response
      pollRetryCount = 0;

      // Check for validation failure
      if (data.all_invalid || data.phase === 'failed_validation') {
        console.log('All contacts failed validation');
        stopStatusPolling();
        showValidationError(data.message || 'All email addresses failed validation');
        updateStatusCard('failed', 'Validation Failed', data.message || 'All contacts failed validation', campaignId);
        return;
      }

      // Check if complete
      if (data.is_complete || data.phase === 'reviewing') {
        console.log('Processing complete');
        stopStatusPolling();

        // Reload the page to show the updated state
        window.location.reload();
        return;
      }

      // Update progress indicators if still processing
      if (data.phase === 'sampling' || data.phase === 'generating') {
        updateProgressBar(data.progress || 50);
      }

      // Check for validation warnings
      if (data.has_validation_issues && data.skipped_count > 0) {
        showValidationWarning(`${data.skipped_count} email(s) failed validation and were skipped`);
      }
    })
    .catch(error => {
      console.error('Polling error:', error);
      pollRetryCount++;

      if (pollRetryCount >= MAX_POLL_RETRIES) {
        console.error('Max retries reached, stopping polling');
        stopStatusPolling();
        showErrorMessage('Connection lost', 'Please refresh the page to check status.');
      }
    });
}

/**
 * Update status card UI.
 */
function updateStatusCard(status, title, message, campaignId) {
  const statusCard = document.getElementById('campaign-status-card');
  const statusTitle = document.getElementById('status-title');
  const statusMessage = document.getElementById('status-message');
  const statusActions = document.getElementById('status-actions');

  if (statusCard) {
    // Remove all status classes
    statusCard.className = statusCard.className.replace(
      /\b(draft|sampling|generating_all|pending_approval|review_pending|scheduled|processing|sending|sent|completed|paused|failed)\b/g,
      ''
    );
    // Add new status class
    statusCard.classList.add(status);
  }

  if (statusTitle) {
    statusTitle.textContent = title;
  }

  if (statusMessage) {
    statusMessage.textContent = message;
  }

  // Update action buttons based on status
  if (statusActions) {
    if (status === 'failed' || status === 'pending_approval') {
      statusActions.innerHTML = `
        <a href="/dashboard/campaigns/${campaignId}/sample/" class="btn btn-gradient-primary">
          <i class="fas fa-eye me-2"></i>View Details
        </a>
      `;
    }
  }
}

/**
 * Update progress bar.
 */
function updateProgressBar(percentage) {
  const progressBar = document.querySelector('.progress-bar-gradient');
  if (progressBar) {
    progressBar.style.width = `${percentage}%`;
  }
}

/**
 * Show validation error alert.
 */
function showValidationError(message) {
  const alertHtml = `
    <div class="validation-error-alert">
      <div class="d-flex align-items-center">
        <i class="fas fa-exclamation-triangle text-danger fa-2x me-3"></i>
        <div>
          <h5 class="mb-1 text-danger">Validation Failed</h5>
          <p class="mb-0">${escapeHtml(message)}</p>
        </div>
      </div>
    </div>
  `;

  const container = document.querySelector('.container-fluid');
  if (container) {
    container.insertAdjacentHTML('afterbegin', alertHtml);
  }
}

/**
 * Show validation warning.
 */
function showValidationWarning(message) {
  if (window.showNotification) {
    window.showNotification('warning', message);
  } else {
    showNotificationLocal('warning', message);
  }
}

/**
 * Show error message alert.
 */
function showErrorMessage(title, message) {
  const alertHtml = `
    <div class="alert alert-danger alert-dismissible fade show" role="alert">
      <strong>${escapeHtml(title)}</strong> ${escapeHtml(message)}
      <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
    </div>
  `;

  const container = document.querySelector('.container-fluid');
  if (container) {
    container.insertAdjacentHTML('afterbegin', alertHtml);
  }
}

/**
 * Toggle Quick Actions Menu.
 */
function toggleQuickActions(button) {
  const quickActions = button.closest('.quick-actions');
  quickActions.classList.toggle('show');

  // Close on outside click
  document.addEventListener('click', function closeMenu(e) {
    if (!quickActions.contains(e.target)) {
      quickActions.classList.remove('show');
      document.removeEventListener('click', closeMenu);
    }
  });
}

/**
 * Animated Counter.
 */
function animateValue(element, start, end, duration) {
  if (start === end) return;
  const range = end - start;
  const increment = end > start ? 1 : -1;
  const stepTime = Math.abs(Math.floor(duration / range));
  let current = start;
  const timer = setInterval(function () {
    current += increment;
    element.textContent = current.toLocaleString();
    if (current === end) {
      clearInterval(timer);
    }
  }, stepTime);
}

/**
 * Preview Email in modal.
 */
async function previewEmail(emailId) {
  const modal = new bootstrap.Modal(document.getElementById('emailModal'));
  const content = document.getElementById('emailContent');

  // Show loading
  content.innerHTML = `
    <div class="text-center py-4">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>
  `;

  modal.show();

  if (!currentCampaignId) {
    content.innerHTML = `
      <div class="alert alert-danger mb-0">
        Failed to load email preview (missing campaign id).
      </div>
    `;
    return;
  }

  try {
    const response = await fetch(
      `/dashboard/campaigns/${currentCampaignId}/emails/${emailId}/preview/`,
      {
        credentials: 'same-origin',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error: ${response.status}`);
    }

    const data = await response.json();
    if (!data?.success || !data?.email) {
      throw new Error(data?.error || 'Invalid preview response');
    }

    const email = data.email;
    const subject = escapeHtml(email.subject || '');
    const recipientName = escapeHtml(email.recipient_name || '');
    const recipientEmail = escapeHtml(email.recipient_email || '');
    const company = escapeHtml(email.company || '');

    const toLine = recipientName
      ? `${recipientName} &lt;${recipientEmail}&gt;`
      : recipientEmail;

    const companySuffix = company
      ? ` <span class="text-muted">(${company})</span>`
      : '';

    const htmlContent = email.html_content || '<p class="text-muted mb-0">No content available.</p>';

    content.innerHTML = `
      <div class="email-preview">
        <div class="mb-3">
          <label class="small text-muted">Subject:</label>
          <div class="fw-semibold">${subject}</div>
        </div>
        <div class="mb-3">
          <label class="small text-muted">To:</label>
          <div>${toLine}${companySuffix}</div>
        </div>
        <hr>
        <div class="email-body">
          ${htmlContent}
        </div>
      </div>
    `;
  } catch (error) {
    content.innerHTML = `
      <div class="alert alert-danger mb-0">
        Failed to load email preview. Please try again.
      </div>
    `;
  }
}

/**
 * Pause Campaign action.
 */
function pauseCampaignAction(campaignId) {
  if (!confirm('Are you sure you want to pause this campaign? You can resume it anytime.')) {
    return;
  }

  showNotificationLocal('info', 'Pausing campaign...');

  fetch(`/campaigns/${campaignId}/pause/`, {
    method: 'POST',
    headers: {
      'X-CSRFToken': getCsrfToken(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      reason: 'Manual pause from detail page'
    })
  })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        showNotificationLocal('success', 'Campaign paused successfully');
        setTimeout(() => location.reload(), 1500);
      } else {
        showNotificationLocal('error', data.error || 'Failed to pause campaign');
      }
    })
    .catch(error => {
      showNotificationLocal('error', 'An error occurred. Please try again.');
    });
}

/**
 * Resume Campaign action.
 */
function resumeCampaignAction(campaignId) {
  if (!confirm('Resume this campaign? Emails will continue sending from where they left off.')) {
    return;
  }

  showNotificationLocal('info', 'Resuming campaign...');

  fetch(`/campaigns/${campaignId}/resume/`, {
    method: 'POST',
    headers: {
      'X-CSRFToken': getCsrfToken(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      reason: 'Manual resume from detail page'
    })
  })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        showNotificationLocal('success', 'Campaign resumed successfully');
        setTimeout(() => location.reload(), 1500);
      } else {
        showNotificationLocal('error', data.error || 'Failed to resume campaign');
      }
    })
    .catch(error => {
      showNotificationLocal('error', 'An error occurred. Please try again.');
    });
}

/**
 * Delete Campaign confirmation.
 */
function confirmDelete() {
  if (!confirm('Are you sure you want to delete this campaign? This action cannot be undone.')) {
    return;
  }
  if (!confirm('This will permanently delete all campaign data. Are you absolutely sure?')) {
    return;
  }
  showNotificationLocal('warning', 'Deleting campaign...');
  // Implement delete logic here
}

/**
 * Local notification function (fallback if global not available).
 */
function showNotificationLocal(type, message) {
  // Try to use global first
  if (window.showNotification) {
    window.showNotification(type, message);
    return;
  }

  const alertClass = {
    'success': 'alert-success',
    'error': 'alert-danger',
    'warning': 'alert-warning',
    'info': 'alert-info'
  }[type] || 'alert-info';

  const alertHtml = `
    <div class="alert ${alertClass} alert-dismissible fade show position-fixed top-0 start-50 translate-middle-x mt-3" style="z-index: 9999;">
      ${escapeHtml(message)}
      <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
    </div>
  `;

  document.body.insertAdjacentHTML('afterbegin', alertHtml);

  // Auto-dismiss after 5 seconds
  setTimeout(() => {
    const alert = document.querySelector('.alert');
    if (alert) {
      alert.remove();
    }
  }, 5000);
}

/**
 * Escape HTML to prevent XSS.
 */
function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

// Auto-initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const config = getConfig('campaign-detail');
  if (config.campaignId) {
    initCampaignDetail(config);
  }
});

// Export for module usage
export default {
  initCampaignDetail,
  startStatusPolling,
  stopStatusPolling,
};

// ============================================
// Test helpers - expose internal state/functions for testing
// ============================================

/**
 * Get the polling URL for a campaign (exposed for testing).
 * @param {string} campaignId - Campaign ID
 * @returns {string} The polling URL
 */
export function getPollUrl(campaignId) {
  return `/dashboard/campaigns/${campaignId}/sample/`;
}

/**
 * Get the pause URL for a campaign (exposed for testing).
 * @param {string} campaignId - Campaign ID
 * @returns {string} The pause URL
 */
export function getPauseUrl(campaignId) {
  return `/campaigns/${campaignId}/pause/`;
}

/**
 * Get the resume URL for a campaign (exposed for testing).
 * @param {string} campaignId - Campaign ID
 * @returns {string} The resume URL
 */
export function getResumeUrl(campaignId) {
  return `/campaigns/${campaignId}/resume/`;
}

/**
 * Get the sample page URL for a campaign (exposed for testing).
 * @param {string} campaignId - Campaign ID
 * @returns {string} The sample page URL
 */
export function getSamplePageUrl(campaignId) {
  return `/dashboard/campaigns/${campaignId}/sample/`;
}

/**
 * Get the current poll interval for testing.
 * @returns {number|null} The interval ID or null
 */
export function getStatusPollIntervalForTesting() {
  return statusPollInterval;
}

/**
 * Get the current retry count for testing.
 * @returns {number} The retry count
 */
export function getPollRetryCountForTesting() {
  return pollRetryCount;
}

/**
 * Reset internal state for testing.
 */
export function resetStateForTesting() {
  if (statusPollInterval) {
    clearInterval(statusPollInterval);
    statusPollInterval = null;
  }
  pollRetryCount = 0;
  currentCampaignId = null;
}

/**
 * Get the max poll retries constant for testing.
 * @returns {number} Max retries
 */
export function getMaxPollRetries() {
  return MAX_POLL_RETRIES;
}

/**
 * Check if a status should trigger polling (exposed for testing).
 * @param {string} status - Campaign status
 * @returns {boolean}
 */
export function shouldStartPolling(status) {
  return ['sampling', 'generating_all'].includes(status);
}
